import {boardCellKey, type CellAddress} from '@github-ui/board-core'
import {hashKey, type QueryClient, useQueryClient} from '@github-ui/react-query'
import {useCallback, useEffect, useState, useSyncExternalStore} from 'react'

import type {MovedCardBuckets} from './keep-moved-cards'
import type {CellPageOutcome} from './types'

type ReadCell = (address: CellAddress) => Promise<CellPageOutcome>

/** As many cell reads as one board reads ahead at once, so one rank cannot send a burst of them. */
const MAX_CELL_READS = 6

const NO_MOVES: ReadonlyMap<string, MovedCardBuckets> = new Map()

/** A cell a board owes a read, and the read in flight for it, if any. */
type OwedRead = {
  address: CellAddress
  /** Increases with each request, so a read started before the latest one does not settle it. */
  requested: number
  reading: {owner: symbol; read: symbol; requested: number} | null
}

/**
 * The cells each board owes a read, by board id. Held with the query client rather than in a
 * board, so it outlives any one board: the cache already holds the order the read replaces, and a
 * board that remounts, or a second board with the same id, has to wait for the read too.
 */
class OwedCellReads {
  #boards = new Map<string, Map<string, OwedRead>>()
  #listeners = new Set<() => void>()
  #requests = 0
  #version = 0

  subscribe = (listener: () => void) => {
    this.#listeners.add(listener)
    return () => {
      this.#listeners.delete(listener)
    }
  }

  getVersion = () => this.#version

  has(boardId: string): boolean {
    return (this.#boards.get(boardId)?.size ?? 0) > 0
  }

  request(boardId: string, address: CellAddress) {
    let cells = this.#boards.get(boardId)
    if (cells === undefined) {
      cells = new Map()
      this.#boards.set(boardId, cells)
    }
    const key = boardCellKey(address)
    const reading = cells.get(key)?.reading ?? null
    cells.set(key, {
      address: {columnKey: address.columnKey, rowKey: address.rowKey},
      requested: ++this.#requests,
      reading,
    })
    this.#notify()
  }

  /**
   * Marks owed reads that are not in flight, or that a newer request replaced, as `owner`'s, up to
   * `limit` in flight. A read replacing one in flight takes its place, since the older is dropped.
   */
  claim(boardId: string, owner: symbol, limit: number): Array<{key: string; address: CellAddress; read: symbol}> {
    const cells = [...(this.#boards.get(boardId) ?? [])]
    let inFlight = cells.filter(([, owed]) => owed.reading !== null).length
    const claimed = []
    for (const [key, owed] of cells) {
      if (owed.reading !== null && owed.reading.requested === owed.requested) continue
      if (owed.reading === null && inFlight >= limit) continue
      if (owed.reading === null) inFlight += 1

      const read = Symbol(key)
      owed.reading = {owner, read, requested: owed.requested}
      claimed.push({key, address: owed.address, read})
    }
    return claimed
  }

  /**
   * Settles one read. A drawn or failed read pays the debt unless a newer request arrived while it
   * was in flight. An abandoned read leaves the debt for the next board to claim.
   */
  settle(boardId: string, key: string, read: symbol, outcome: CellPageOutcome) {
    const cells = this.#boards.get(boardId)
    const owed = cells?.get(key)
    if (cells === undefined || owed === undefined || owed.reading?.read !== read) return

    if (outcome !== 'abandoned' && owed.reading.requested === owed.requested) cells.delete(key)
    else owed.reading = null
    if (cells.size === 0) this.#boards.delete(boardId)
    this.#notify()
  }

  /** Gives up every read `owner` has in flight, for a board that is going away. */
  release(boardId: string, owner: symbol) {
    let released = false
    for (const owed of this.#boards.get(boardId)?.values() ?? []) {
      if (owed.reading?.owner !== owner) continue
      owed.reading = null
      released = true
    }
    if (released) this.#notify()
  }

  #notify() {
    this.#version += 1
    for (const listener of this.#listeners) listener()
  }
}

/**
 * The cards each board moved between cells, and the buckets each move put them in, by board id.
 * Kept beside the reads it protects, so a board that claims a read its predecessor owed keeps the
 * cards that predecessor moved when the page lags the move.
 */
class BoardMoves {
  #boards = new Map<string, ReadonlyMap<string, MovedCardBuckets>>()

  remember(boardId: string, itemId: string, buckets: MovedCardBuckets) {
    this.#boards.set(boardId, new Map(this.of(boardId)).set(itemId, buckets))
  }

  of(boardId: string): ReadonlyMap<string, MovedCardBuckets> {
    return this.#boards.get(boardId) ?? NO_MOVES
  }

  forget(boardId: string) {
    this.#boards.delete(boardId)
  }

  get isEmpty(): boolean {
    return this.#boards.size === 0
  }
}

const memories = new WeakMap<QueryClient, {reads: OwedCellReads; moves: BoardMoves}>()

function memoryFor(queryClient: QueryClient) {
  const existing = memories.get(queryClient)
  if (existing !== undefined) return existing

  const memory = {reads: new OwedCellReads(), moves: new BoardMoves()}
  memories.set(queryClient, memory)
  // The moves describe cards cached under the board's key, so they go when those cards do.
  // Checked first, so a page that moved nothing never hashes the keys of the queries it drops.
  queryClient.getQueryCache().subscribe(event => {
    if (event.type === 'removed' && !memory.moves.isEmpty) memory.moves.forget(hashKey(event.query.queryKey))
  })
  return memory
}

/** The cards each board moved, which outlive any one board the way its owed reads do. */
export function boardMovesFor(queryClient: QueryClient): BoardMoves {
  return memoryFor(queryClient).moves
}

/**
 * Reloads cells of one board, and says whether any reload is still owed. It reads a few cells at
 * once, and the rest as each lands.
 *
 * What is owed outlives the board that asked. A board that unmounts drops its reads, and the next
 * board with the same `boardId` reads those cells itself. Every board with that id answers
 * `isReloading` until each owed cell has been drawn or has failed to load. A new `boardId` starts
 * with nothing owed.
 */
export function useBoardCellReloads({
  boardId,
  readCell,
}: {
  boardId: string
  /** Reads one cell again. Absent where the board cannot read a cell, which owes nothing. */
  readCell: ReadCell | undefined
}): {
  /** Owes a read of one cell to `board`, which is this board unless a gesture names another. */
  reloadCell: (address: CellAddress, board?: string) => void
  isReloading: boolean
} {
  const {reads} = memoryFor(useQueryClient())
  const [owner] = useState(() => Symbol('board cell reloads'))
  const version = useSyncExternalStore(reads.subscribe, reads.getVersion, () => 0)
  const isReloading = useSyncExternalStore(
    reads.subscribe,
    () => reads.has(boardId),
    () => false,
  )

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- claims reads owed by the store, including ones a board that unmounted left behind
    if (readCell === undefined) return
    for (const {key, address, read} of reads.claim(boardId, owner, MAX_CELL_READS)) {
      const settle = async () => reads.settle(boardId, key, read, await readCell(address))
      void settle()
    }
  }, [reads, version, boardId, owner, readCell])

  useEffect(() => () => reads.release(boardId, owner), [reads, boardId, owner])

  const reloadCell = useCallback(
    (address: CellAddress, board = boardId) => {
      if (readCell !== undefined) reads.request(board, address)
    },
    [reads, boardId, readCell],
  )

  return {reloadCell, isReloading}
}
